import React, { useEffect, useState } from 'react';

import { CloudAccountInfoGetResponse } from '@ariksa/jit/api';
import { Box, Center, HStack, Stack, Text } from '@chakra-ui/react';
import { filter } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { INIT_PAGE_INFO } from 'services/utils/constants';
import { customTheme } from 'theme';

import { FormFieldBox, getIcon } from 'components/DataDisplay';
import { CancelButton, Form, PrimaryButton } from 'components/DataEntry';
import { DocumentsIcon, RoleIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { jitFormStyles } from 'containers/Jit/Setup/Components/styles';
import { JITConfigurationModalHeader } from 'containers/Jit/Setup/Components/Utils/JITConfigurationModalHeader';
import { JITInformationAlert } from 'containers/Jit/Setup/Components/Utils/JITInformationAlert';
import { selectJitSetup } from 'containers/Jit/Setup/selectors';
import { actions } from 'containers/Jit/Setup/slice';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

interface JitRoleConfigProps {
  isOpen: boolean;
  onClose: () => void;
  account: CloudAccountInfoGetResponse;
}

const JitRoleConfig = (props: JitRoleConfigProps) => {
  const dispatch = useDispatch();
  const { isOpen, onClose, account } = props;
  const { roles, updateJitRoles } = useSelector(selectJitSetup);
  const [roleOptions, setRoleOptions] = useState<Record<string, any>[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Record<string, any>[]>([]);
  const [isChanged, setIsChanged] = useState(false);
  const { accountMapping } = useCloudAccountId();

  useEffect(() => {
    const options = map(roles.data.roles, o => ({
      label: o?.role,
      value: o?.role_id ?? o?.role,
      data: o,
    }));
    setRoleOptions(options);
    setSelectedRoles(filter(options, o => o.data.jit_enabled));
  }, [roles.data.roles]);

  const handleSummit = data => {
    dispatch(
      actions.updateJitRoles({
        q: {
          accountId: account.uuid,
          jitRolesUpdateRequest: {
            jit_roles: data.roles.map(r => r.value),
          },
        },
        onSuccess: () => {
          onClose();
          dispatch(actions.getJitAccounts({ q: {}, page: INIT_PAGE_INFO }));
        },
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <JITConfigurationModalHeader
          account={account}
          label="Allow JIT Roles"
          icon={<RoleIcon />}
          iconColor={customTheme.colors.gray['300']}
        />
      }
      size="lg"
      styles={{ body: { pb: 0, px: 6 } }}
      body={
        <Stack w="full" h="full" spacing={5}>
          <JITInformationAlert
            icon={<DocumentsIcon color="primary" />}
            description="Ensure that roles have right-sized permissions to cloud resources"
          />
          <Box px={12}>
            <Form
              schema={{
                account: {
                  type: 'custom-with-form-control',
                  label: 'Account',
                  component: (
                    <FormFieldBox>
                      <HStack fontSize="15px" w="full">
                        <Box boxSize={5}>
                          <Center>{getIcon(account?.cloud_type)}</Center>
                        </Box>
                        <Box
                          color={customTheme.colors.gray['300']}
                          flex={1}
                          minW={0}
                        >
                          <Text
                            w="full"
                            minW={0}
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                          >
                            {accountMapping?.[account.uuid]?.name}:{' '}
                            {account.cloud_account_id}
                          </Text>
                        </Box>
                      </HStack>
                    </FormFieldBox>
                  ),
                },
                roles: {
                  type: 'react-select',
                  label: 'Select roles for JIT',
                  isMulti: true,
                  showTotalSelected: true,
                  showSelectedAtBottom: true,
                  selectedOptionsStyles: {
                    maxH: 32,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'componentBorder',
                    borderRadius: 6,
                    p: 2,
                  },
                  value: selectedRoles,
                  options: roleOptions,
                  isLoading: roles.isLoading,
                  onChange: val => {
                    setSelectedRoles(val);
                    setIsChanged(true);
                  },
                  placeholder: 'Select roles',
                },
              }}
              styles={jitFormStyles}
              handleSubmit={handleSummit}
              buttonOptions={{ submit: { name: 'Ok', isVisible: false } }}
            />
          </Box>
        </Stack>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton onClick={onClose}>CANCEL</CancelButton>
          <PrimaryButton
            px={5}
            onClick={handleSummit}
            isLoading={updateJitRoles.isLoading}
            isDisabled={!selectedRoles.length}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
    />
  );
};

export default JitRoleConfig;
